import { useCallback, useEffect, useState } from 'react';
import excalidrawService from '../services/excalidrawService';
import i18n from '../i18n/config';
import { ExcalidrawData } from '../components/Common/ExcalidrawCanvas';

type ParentObjectType = 'use-case' | 'cadrage' | 'audit';

interface ParentObject {
  id: string | undefined;
  type: ParentObjectType;
}

interface UseExcalidrawPersistenceResult {
  data: ExcalidrawData | null;
  isEditing: boolean;
  isLoading: boolean;
  error: string | null;
  setData: (value: ExcalidrawData) => void;
  toggleEditing: () => void;
  fetchDiagram: () => Promise<void>;
  saveDiagram: () => Promise<void>;
}

// We reuse the existing backend endpoints and DB fields that were originally
// designed for ReactFlow. To avoid DB/schema changes, we store the Excalidraw
// JSON inside flow_data.nodes[0].excalidraw and ignore edges.

export const useExcalidrawPersistence = (
  parent: ParentObject
): UseExcalidrawPersistenceResult => {
  const [data, setData] = useState<ExcalidrawData | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getApiEndpoint = useCallback((): string | null => {
    if (!parent.id) return null;
    switch (parent.type) {
      case 'use-case':
        return `/use-cases/${parent.id}/react-flow/`;
      case 'cadrage':
        return `/cadrages/${parent.id}/architecture-flow/`;
      default:
        return null;
    }
  }, [parent.id, parent.type]);

  const fetchDiagram = useCallback(async () => {
    const endpoint = getApiEndpoint();
    if (!endpoint) {
      setData(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      let response: any;
      if (parent.type === 'use-case' && parent.id) {
        response = await excalidrawService.getUseCaseData(parent.id);
      } else if (parent.type === 'cadrage' && parent.id) {
        response = await excalidrawService.getCadrageData(parent.id);
      } else {
        throw new Error(i18n.t('errors.diagram.unsupportedParent'));
      }
      const flowData = response?.flow_data;
      const stored = flowData?.nodes?.[0]?.excalidraw as ExcalidrawData | undefined;
      if (stored && typeof stored === 'object') {
        const cleaned: ExcalidrawData = {
          ...stored,
          appState: stored.appState ? { ...stored.appState } : undefined,
        };
        // Excalidraw expects collaborators to be an iterable (Map-like).
        // If it's been serialized into a plain object or other shape, drop it.
        if (
          cleaned.appState &&
          (cleaned.appState as any).collaborators &&
          typeof (cleaned.appState as any).collaborators.forEach !== 'function'
        ) {
          delete (cleaned.appState as any).collaborators;
        }
        setData(cleaned);
      } else {
        setData(null);
      }
    } catch (err: any) {
      console.error(`Failed to fetch Excalidraw diagram for ${parent.type} ${parent.id}:`, err);
      setError(err.response?.data?.detail || err.message || i18n.t('errors.diagram.loadFailed'));
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [getApiEndpoint, parent.id, parent.type]);

  useEffect(() => {
    fetchDiagram();
  }, [fetchDiagram]);

  const saveDiagram = useCallback(async () => {
    const endpoint = getApiEndpoint();
    if (!endpoint || !parent.id) {
      setError(i18n.t('errors.diagram.saveMissingParent'));
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const safeData: ExcalidrawData | null = data
        ? {
            ...data,
            appState: data.appState
              ? {
                  ...data.appState,
                  // Avoid persisting collaborators in a shape Excalidraw can't rehydrate
                  collaborators: undefined,
                }
              : undefined,
          }
        : null;

      const payload: { flow_data: { nodes: any[]; edges: any[] }; [key: string]: any } = {
        flow_data: {
          // Store the Excalidraw JSON inside the first node so we keep using
          // the existing DB table/columns transparently.
          nodes: safeData ? [{ excalidraw: safeData }] : [],
          edges: [],
        },
      };
      if (parent.type === 'use-case') payload.use_case_id = parent.id;
      else if (parent.type === 'cadrage') payload.cadrage_id = parent.id;
      else if (parent.type === 'audit') payload.audit_id = parent.id;

      if (parent.type === 'use-case' && parent.id) {
        await excalidrawService.saveUseCaseData(parent.id, payload);
      } else if (parent.type === 'cadrage' && parent.id) {
        await excalidrawService.saveCadrageData(parent.id, payload);
      } else {
        throw new Error(i18n.t('errors.diagram.unsupportedParent'));
      }
    } catch (err: any) {
      console.error(`Failed to save Excalidraw diagram for ${parent.type} ${parent.id}:`, err);
      setError(err.response?.data?.detail || err.message || i18n.t('errors.diagram.saveFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [data, getApiEndpoint, parent.id, parent.type]);

  const toggleEditing = useCallback(() => {
    setIsEditing((prev) => !prev);
  }, []);

  return {
    data,
    isEditing,
    isLoading,
    error,
    setData,
    toggleEditing,
    fetchDiagram,
    saveDiagram,
  };
};


